/* Atlas V3 Desktop — ⌘K command palette.
   A sleek spotlight search (cmdk/Aceternity-flavored, studio-tokened): live
   ticker results + jump-to-page actions, full keyboard nav, recents. Replaces
   the mobile AtlasSearch slide-down on desktop. Mobile is untouched. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useIsAdmin } from '@/v2/hooks/useIsAdmin'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const RECENT_KEY = 'atlas_v3web_recent'

const PAGE_ACTIONS = [
  { k: 'overview', label: 'Overview' },
  { k: 'signals', label: 'Signals' },
  { k: 'markets', label: 'Markets' },
  { k: 'mystocks', label: 'My Stocks' },
  { k: 'insights', label: 'Insights' },
  { k: 'calendar', label: 'Calendar' },
  { k: 'agent', label: 'My Agent' },
  { k: 'settings', label: 'Settings' },
]

function loadRecents() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, 6) } catch { return [] }
}
function pushRecent(t) {
  try {
    const next = [t, ...loadRecents().filter((x) => x !== t)].slice(0, 6)
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch { /* private mode */ }
}

export function CommandPalette({ open, onClose, openTicker, onNav }) {
  const { stocks } = useAtlasStocks({ includeInactive: true })
  const isAdmin = useIsAdmin()
  const pageActions = useMemo(
    () => (isAdmin ? [...PAGE_ACTIONS, { k: 'admin', label: 'Admin' }] : PAGE_ACTIONS),
    [isAdmin],
  )
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [recents, setRecents] = useState([])
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const universe = useMemo(
    () => stocks.map((s) => ({ t: s.ticker, n: s.name || s.company || '', sector: s.sector || '' })),
    [stocks],
  )
  const nameByT = useMemo(() => Object.fromEntries(universe.map((u) => [u.t, u])), [universe])

  useEffect(() => {
    if (open) { setQ(''); setActive(0); setRecents(loadRecents()); requestAnimationFrame(() => inputRef.current?.focus()) }
  }, [open])

  // Ranked matches: ticker-prefix > ticker-substring > name-substring, capped.
  const results = useMemo(() => {
    const query = q.trim().toUpperCase()
    if (!query) return []
    const pre = [], sub = [], nam = []
    for (const u of universe) {
      const T = u.t.toUpperCase(), N = u.n.toUpperCase()
      if (T.startsWith(query)) pre.push(u)
      else if (T.includes(query)) sub.push(u)
      else if (N.includes(query)) nam.push(u)
    }
    return [...pre, ...sub, ...nam].slice(0, 8)
  }, [q, universe])

  // Flatten to a single keyboard-navigable list of rows.
  const rows = useMemo(() => {
    if (q.trim()) {
      return [
        ...results.map((r) => ({ type: 'ticker', ...r })),
        ...pageActions.filter((a) => a.label.toUpperCase().includes(q.trim().toUpperCase())).map((a) => ({ type: 'page', ...a })),
      ]
    }
    return [
      ...recents.map((t) => ({ type: 'ticker', t, n: nameByT[t]?.n || '', sector: nameByT[t]?.sector || '', recent: true })),
      ...pageActions.map((a) => ({ type: 'page', ...a })),
    ]
  }, [q, results, recents, nameByT, pageActions])

  useEffect(() => { setActive(0) }, [q])

  const choose = (row) => {
    if (!row) return
    if (row.type === 'ticker') { pushRecent(row.t); openTicker(row.t) }
    else { onNav(row.k) }
    onClose()
  }

  useEffect(() => {
    if (!open) return
    const h = (e) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(rows.length - 1, i + 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)) }
      else if (e.key === 'Enter') { e.preventDefault(); choose(rows[active]) }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, rows, active]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the active row in view.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-i="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (!open) return null

  const showLabel = !q.trim()
  let sawTicker = false, sawPage = false

  return (
    <div className="v3web-cmdk" onMouseDown={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '13vh',
      background: 'hsl(var(--studio-overlay) / 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      animation: 'v3webCmdFade 140ms ease',
    }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{
        width: 'min(600px, 92vw)', background: 'hsl(var(--studio-surface))',
        border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)',
        boxShadow: '0 32px 80px -24px hsl(var(--studio-overlay) / 0.9), 0 0 0 1px hsl(var(--studio-overlay) / 0.4)',
        overflow: 'hidden', animation: 'v3webCmdIn 200ms var(--studio-ease)',
      }}>
        {/* input row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid hsl(var(--studio-border) / 0.7)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="hsl(var(--studio-text-subtle))" strokeWidth="1.8" style={{ width: 17, height: 17, flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search the universe"
            spellCheck={false}
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'hsl(var(--studio-text))', fontSize: 15, fontFamily: 'var(--studio-font-sans)' }}
          />
          <kbd style={{ ...Mono, fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', border: '1px solid hsl(var(--studio-border))', borderRadius: 4, padding: '2px 6px' }}>ESC</kbd>
        </div>

        {/* results */}
        <div ref={listRef} className="atlas-scroll" style={{ maxHeight: 'min(52vh, 420px)', overflowY: 'auto', padding: '8px 0' }}>
          {rows.length === 0 && (
            <div style={{ padding: '26px 18px', textAlign: 'center', color: 'hsl(var(--studio-text-subtle))', fontSize: 13 }}>
              No matches for “{q.trim()}”.
            </div>
          )}
          {rows.map((row, i) => {
            const isActive = i === active
            let header = null
            if (row.type === 'ticker' && !sawTicker) { sawTicker = true; header = row.recent ? 'RECENT' : (showLabel ? 'RECENT' : 'TICKERS') }
            if (row.type === 'page' && !sawPage) { sawPage = true; header = showLabel ? 'JUMP TO' : 'PAGES' }
            return (
              <div key={`${row.type}-${row.t || row.k}-${i}`}>
                {header && <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-text-subtle))', padding: '10px 20px 6px' }}>{header}</div>}
                <div
                  data-i={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(row)}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px', cursor: 'pointer',
                    background: isActive ? 'hsl(var(--studio-surface-2))' : 'transparent',
                  }}
                >
                  {isActive && <span style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 2, borderRadius: 2, background: 'hsl(var(--studio-accent))' }} />}
                  {row.type === 'ticker' ? (
                    <>
                      <StockLogo ticker={row.t} size={22} />
                      <span style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', minWidth: 52 }}>{row.t}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'hsl(var(--studio-text-muted))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.n}</span>
                      {row.sector && <span style={{ ...Mono, fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', letterSpacing: '0.04em' }}>{row.sector}</span>}
                    </>
                  ) : (
                    <>
                      <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'hsl(var(--studio-text-subtle))' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 15, height: 15 }}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                      </span>
                      <span style={{ flex: 1, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{row.label}</span>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* footer hints */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '9px 18px', borderTop: '1px solid hsl(var(--studio-border) / 0.7)', ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))' }}>
          <span><b style={{ color: 'hsl(var(--studio-text-muted))' }}>↑↓</b> navigate</span>
          <span><b style={{ color: 'hsl(var(--studio-text-muted))' }}>↵</b> open</span>
          <span><b style={{ color: 'hsl(var(--studio-text-muted))' }}>esc</b> close</span>
        </div>
      </div>
      <style>{'@keyframes v3webCmdFade{from{opacity:0}to{opacity:1}}@keyframes v3webCmdIn{from{opacity:0;transform:translateY(-6px) scale(0.985)}to{opacity:1;transform:none}}'}</style>
    </div>
  )
}
